import * as d3 from "d3";
import type { FeatureCollection } from "geojson";
import type { MapDestination } from "../types";

export const MAP_WIDTH = 900;
export const MAP_HEIGHT = 520;

export const scaleValue = (base: number, scale: number) => base * scale;

export async function loadWorldGeojson(signal?: AbortSignal) {
  try {
    return (await d3.json("/assets/world.geojson", { signal })) as FeatureCollection | undefined;
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") {
      return undefined;
    }
    throw error;
  }
}

export function drawBaseLand(
  container: d3.Selection<SVGGElement, unknown, null, undefined>,
  geo: FeatureCollection,
  projection: d3.GeoProjection,
  options: { fill?: string; stroke?: string; strokeWidth?: number } = {},
) {
  const { fill = "#e2e8f0", stroke = "#cbd5f5", strokeWidth = 0.6 } = options;

  container
    .append("g")
    .attr("class", "map-land")
    .selectAll("path")
    .data(geo.features)
    .join("path")
    .attr("d", d3.geoPath().projection(projection))
    .attr("fill", fill)
    .attr("stroke", stroke)
    .attr("stroke-width", strokeWidth);
}

export type LegendItem = { label: string; color: string };

type Coordinate = { lat: number; lon: number };

export function getDestinationDisplayLabel(destination: MapDestination): string {
  return destination.destinationCountry ?? destination.destinationCity ?? destination.key;
}

export function groupDestinationsByCountry(destinations: MapDestination[]): MapDestination[] {
  const grouped = new Map<
    string,
    {
      key: string;
      destinationCountry: string | null;
      destinationCity: string | null;
      destination: Coordinate | null;
      origin: Coordinate | null;
      compliant: number;
      nonCompliant: number;
      low: number;
      ok: number;
      high: number;
      expedition: number;
      recovered: number;
      consignmentIds: Set<string>;
    }
  >();

  for (const destination of destinations) {
    const country = normalizeLabel(destination.destinationCountry);
    const city = normalizeLabel(destination.destinationCity);
    const label = country ?? city ?? destination.key;
    const groupKey = country ? `country:${country}` : `fallback:${label}`;

    let group = grouped.get(groupKey);
    if (!group) {
      group = {
        key: groupKey,
        destinationCountry: country,
        destinationCity: country ? null : city,
        destination: null,
        origin: null,
        compliant: 0,
        nonCompliant: 0,
        low: 0,
        ok: 0,
        high: 0,
        expedition: 0,
        recovered: 0,
        consignmentIds: new Set<string>(),
      };
      grouped.set(groupKey, group);
    }

    group.destination = group.destination ?? destination.destination;
    group.origin = group.origin ?? destination.origin;

    group.compliant += destination.counts.compliant;
    group.nonCompliant += destination.counts.nonCompliant;
    group.low += destination.lowOkHigh.low;
    group.ok += destination.lowOkHigh.ok;
    group.high += destination.lowOkHigh.high;
    group.expedition += destination.expedition ?? 0;
    group.recovered += destination.recovered ?? 0;

    destination.consignmentIds.forEach((id) => {
      const trimmed = id.trim();
      if (trimmed) {
        group!.consignmentIds.add(trimmed);
      }
    });
  }

  return Array.from(grouped.values()).map((group) => ({
    key: group.key,
    destinationCity: group.destinationCity,
    destinationCountry: group.destinationCountry,
    destination: group.destination,
    origin: group.origin,
    counts: {
      compliant: group.compliant,
      nonCompliant: group.nonCompliant,
    },
    lowOkHigh: {
      low: group.low,
      ok: group.ok,
      high: group.high,
    },
    expedition: group.expedition,
    recovered: group.recovered,
    consignmentIds: Array.from(group.consignmentIds),
  }));
}

function normalizeLabel(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
}

export function drawLegend(
  svg: d3.Selection<SVGSVGElement | null, unknown, null, undefined>,
  items: readonly LegendItem[],
  scale = 1,
  offset: { x?: number; y?: number } = {},
) {
  if (!items.length) return;

  const OFFSET_X = offset.x ?? scaleValue(18, scale);
  const OFFSET_Y = offset.y ?? scaleValue(18, scale);
  const BOX = scaleValue(12, scale);
  const GAP_Y = scaleValue(6, scale);
  const FONT = scaleValue(12, scale);
  const PAD = scaleValue(8, scale);

  const legend = svg
    .append("g")
    .attr("class", "map-legend")
    .attr("transform", `translate(${OFFSET_X}, ${OFFSET_Y})`)
    .style("pointer-events", "none");

  const bg = legend
    .append("rect")
    .attr("rx", scaleValue(6, scale))
    .attr("ry", scaleValue(6, scale))
    .attr("fill", "#ffffff")
    .attr("stroke", "#e2e8f0")
    .attr("stroke-width", scaleValue(1, scale))
    .attr("opacity", 1);

  const content = legend.append("g").attr("transform", `translate(${PAD}, ${PAD})`);

  const rows = content
    .selectAll("g.legend-row")
    .data(items)
    .join("g")
    .attr("class", "legend-row")
    .attr("transform", (_, index) => `translate(0, ${index * (BOX + GAP_Y)})`);

  rows
    .append("rect")
    .attr("width", BOX)
    .attr("height", BOX)
    .attr("rx", scaleValue(3, scale))
    .attr("fill", (d) => d.color);

  rows
    .append("text")
    .attr("x", BOX + scaleValue(8, scale))
    .attr("y", BOX - scaleValue(2, scale))
    .attr("fill", "#1f2937")
    .attr("font-size", FONT)
    .text((d) => d.label);

  const bbox = (content.node() as SVGGElement).getBBox();
  bg.attr("width", bbox.width + PAD * 2).attr("height", bbox.height + PAD * 2);
}
